import { ValidationMessage } from "../../../apis/ValidationMessage";
import { ToastConfig } from "../../../config/ToastConfig";
import { ErrorToast, InfoToast, SuccessToast, ToastLegacy, ToastMessage, ToastType, WarningToast } from "../../../entities/toasts/Toasts";
import { getUniqueIdentifier } from "../../../utils/GUID";
import { TIMING_UTILITIES } from "../../../utils/TimingUtilities";
import { AppThunkAction } from "../ApplicationState";

export type ToastPush = { type: 'TOAST_PUSH', id: string, message: ToastMessage, toastType: ToastType }
export type ToastPushLegacy = { type: 'TOAST_PUSH_LEGACY', toast: ToastLegacy };
export type ToastPushRange = { type: 'TOAST_PUSH_RANGE', toasts: ToastLegacy[] };
export type ToastDismiss = { type: 'TOAST_DISMISS', id: string }
export type ToastAction = ToastPush
    | ToastPushLegacy
    | ToastPushRange
    | ToastDismiss;

export const ToastActions = {
    toastSuccess: (message: ToastMessage, duration: number = ToastConfig.ToastDuration): AppThunkAction<ToastAction> =>
        (dispatch) => ToastDispatchables.toastLegacy(new SuccessToast(message), dispatch, duration),

    toastInfo: (message: ToastMessage, duration: number = ToastConfig.ToastDuration): AppThunkAction<ToastAction> =>
        (dispatch) => ToastDispatchables.toastLegacy(new InfoToast(message), dispatch, duration),

    toastWarning: (message: ToastMessage, duration: number = ToastConfig.ToastDuration): AppThunkAction<ToastAction> =>
        (dispatch) => ToastDispatchables.toastLegacy(new WarningToast(message), dispatch, duration),

    toastError: (message: ToastMessage, duration: number = ToastConfig.ToastDuration): AppThunkAction<ToastAction> =>
        (dispatch) => ToastDispatchables.toastLegacy(new ErrorToast(message), dispatch, duration),

    dismissToast: (id: string): AppThunkAction<ToastDismiss> =>
        (dispatch) => dispatch({ type: 'TOAST_DISMISS', id }),
}

type ToastDispatchable = (action: ToastAction) => void;

const toast = async (dispatch: ToastDispatchable, message: ToastMessage, toastType: ToastType, duration: number = ToastConfig.ToastDuration): Promise<void> => {
    const id = getUniqueIdentifier();
    dispatch({ type: 'TOAST_PUSH', id, message, toastType });
    await TIMING_UTILITIES.delay(duration);
    dispatch({ type: 'TOAST_DISMISS', id })
};

export const ToastDispatchables = {
    toastSuccess: async (dispatch: ToastDispatchable, message: ToastMessage, duration: number = ToastConfig.ToastDuration): Promise<void> =>
        toast(dispatch, message, 'success', duration),
    
    toastError: async (dispatch: ToastDispatchable, message: ToastMessage, duration: number = ToastConfig.ToastDuration): Promise<void> =>
        toast(dispatch, message, 'error', duration),
    
    toastWarning: async (dispatch: ToastDispatchable, message: ToastMessage, duration: number = ToastConfig.ToastDuration): Promise<void> =>
        toast(dispatch, message, 'warning', duration),

    toastInfo: async (dispatch: ToastDispatchable, message: ToastMessage, duration: number = ToastConfig.ToastDuration): Promise<void> =>
        toast(dispatch, message, 'info', duration),

    toastLegacy: async (toast: ToastLegacy, dispatch: ToastDispatchable, duration: number = ToastConfig.ToastDuration): Promise<void> => {
        dispatch({ type: 'TOAST_PUSH_LEGACY', toast });
        await TIMING_UTILITIES.delay(duration);
        dispatch({ type: 'TOAST_DISMISS', id: toast.id });
    },

    toastValidationResults: (validationResults: ValidationMessage[], dispatch: ToastDispatchable, duration: number = ToastConfig.ToastDuration): void => {
        for (let validationResult of validationResults) {
            ToastDispatchables.toastError(dispatch, validationResult.message, duration);
        }
    }
}